import React from "react";
import formatCustomDate from "../helpers/customDateFormat";
import {
  getOverallDenominationCounts,
  getOverallReturnAmount,
  isThaaiMamanEntry,
  getTamilAssetTypeLabel,
  sortEntriesForExport,
  formatCurrencyTamil,
  formatCurrency
} from "../helpers/exportHelpers";

const ThermalReport = React.forwardRef((props, ref) => {
  const { eventDetails, entries = [], stats = {} } = props;

  const vendorName = eventDetails?.vendor_name || "Digital Moi Tech";
  const vendorPhone = eventDetails?.vendor_mobile || "";
  const vendorAddress = eventDetails?.vendor_address || "";
  
  const customerName = eventDetails?.customer_name || "-";
  const customerMobile = eventDetails?.customer_mobile || "";
  const eventType = eventDetails?.event_type || "-";
  const eventDate = formatCustomDate(eventDetails?.event_date, "date") || "-";
  const eventAddress = eventDetails?.event_address || "-";
  
  const operatorName = eventDetails?.operator_name || "-";

  const printedAt = new Date().toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  const denominationCounts = getOverallDenominationCounts(entries);
  const denomEntries = Object.entries(denominationCounts)
    .map(([note, count]) => ({
      note: parseInt(note, 10) || 0,
      count: parseInt(count, 10) || 0,
    }))
    .filter((entry) => entry.note > 0 && entry.count > 0)
    .sort((a, b) => b.note - a.note);

  const totalReturnAmount = getOverallReturnAmount(entries);
  const hasDenominations = denomEntries.length > 0;

  const sortedEntries = sortEntriesForExport(entries);

  return (
    <div
      ref={ref}
      className="bg-white max-w-[80mm] mx-auto p-1 font-sans text-[9px] leading-tight text-black font-normal"
    >
      {/* VENDOR HEADER */}
      <div className="text-center border-b border-black pb-1 mb-1">
        <h1 className="text-[12px] font-medium uppercase tracking-wider m-0">{vendorName}</h1>
        <p className="text-[10px] mt-0.5">{[vendorAddress, vendorPhone].filter(Boolean).join(" | ")}</p>
        <p className="uppercase text-[10px] tracking-widest mt-0.5 font-medium">Overall Collection Report</p>
      </div>

      {/* EVENT INFO */}
      <div className="border border-black rounded p-1 mb-1 text-center">
        <h2 className="text-[12px] m-0 font-medium">{customerName}</h2>
        {customerMobile && <p className="text-[10px] mt-0.5">{customerMobile}</p>}
        <div className="h-px bg-black my-0.5 opacity-50"></div>
        <p className="text-[11px] uppercase font-medium">{eventType}</p>
        <p className="text-[10px] mt-0.5">{eventDate}</p>
        {eventAddress && <p className="text-[10px] mt-0.5">{eventAddress}</p>}
      </div>

      {/* OVERALL SUMMARY */}
      <div className="mb-1 border border-black rounded p-1">
        <div className="text-center border-b border-gray-300 mb-0.5 pb-0.5 uppercase tracking-wide text-[10px] font-medium">Summary</div>
        <div className="flex justify-between border-b border-gray-300 py-0.5">
          <span>Total Entries:</span><span className="text-right">{stats.totalEntries || 0}</span>
        </div>
        <div className="flex justify-between border-b border-gray-300 py-0.5">
          <span>Total Cash:</span><span className="text-right">{formatCurrency(stats.totalCash || 0)}</span>
        </div>
        <div className="flex justify-between border-b border-gray-300 py-0.5">
          <span>Total Online:</span><span className="text-right">{formatCurrency(stats.totalOnline || 0)}</span>
        </div>
        <div className="flex justify-between items-center border-y border-black py-1 mt-0.5 text-[11px] font-medium">
          <span>Total Amount:</span>
          <span>{formatCurrency(stats.totalMonetary || 0)}</span>
        </div>
        <div className="flex justify-between border-b border-gray-300 py-0.5 mt-0.5">
          <span>Ornaments:</span><span className="text-right">{stats.ornamentsCount || 0}</span>
        </div>
        <div className="flex justify-between py-0.5">
          <span>Things:</span><span className="text-right">{stats.thingsCount || 0}</span>
        </div>
      </div>

      {/* THAAI MAMAN SUMMARY */}
      {(stats.thaaiMamanCount > 0) && (
        <div className="mb-1 border border-black rounded p-1">
          <div className="text-center border-b border-gray-300 mb-0.5 pb-0.5 uppercase tracking-wide text-[10px] font-medium">Thaai Maman Summary</div>
          <div className="flex justify-between border-b border-gray-300 py-0.5">
            <span>Total Entries:</span><span className="text-right">{stats.thaaiMamanCount || 0}</span>
          </div>
          <div className="flex justify-between py-0.5 font-medium">
            <span>Total Amount:</span><span className="text-right">{formatCurrency(stats.thaaiMamanTotal || 0)}</span>
          </div>
        </div>
      )}

      {/* OVERALL DENOMINATION */}
      {hasDenominations && (
        <div className="mb-1 border border-black rounded p-1">
          <div className="text-center border-b border-gray-300 mb-0.5 pb-0.5 uppercase tracking-wide text-[10px] font-medium">Overall Denominations</div>
          <table className="w-full text-[10px]">
            <tbody>
              {denomEntries.map(({ note, count }) => (
                <tr key={note}>
                  <td className="font-mono py-0.5 text-left">{note}</td>
                  <td className="font-mono py-0.5 text-center">x {count}</td>
                  <td className="font-mono py-0.5 text-right">{note * count}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="flex justify-between border-t border-dashed border-gray-400 pt-0.5 mt-0.5 text-[11px] font-medium">
            <span>Total Denom:</span>
            <span>{formatCurrency((stats.totalMonetary || 0) + (totalReturnAmount || 0))}</span>
          </div>
          {totalReturnAmount > 0 && (
             <div className="flex justify-between border-t border-dashed border-gray-400 pt-0.5 mt-0.5 text-[10px]">
               <span>Total Returns:</span>
               <span>{formatCurrency(totalReturnAmount)}</span>
             </div>
          )}
        </div>
      )}

      {/* ALL ENTRIES */}
      <div className="mb-1 border-t-2 border-black mt-2 pt-1">
        <div className="text-center border-b border-black mb-1 pb-0.5 uppercase tracking-wide text-[12px] font-medium">All Entries</div>
        <table className="w-full text-[9px] border-collapse">
          <thead>
            <tr className="border-b border-black">
              <th className="text-left pb-0.5">#</th>
              <th className="text-left pb-0.5">Name</th>
              <th className="text-right pb-0.5">Amount/Asset</th>
            </tr>
          </thead>
          <tbody>
            {sortedEntries.map((entry, idx) => (
              <tr key={entry.id || idx} className="border-b border-gray-200">
                <td className="py-0.5 align-top">{idx + 1}</td>
                <td className="py-0.5 align-top">
                  <div className="text-[10px]">{entry.name}</div>
                  <div className="text-[8px] text-gray-800">{entry.village || entry.city} {isThaaiMamanEntry(entry) && "(Thaai Maman)"}</div>
                </td>
                <td className="py-0.5 align-top text-right whitespace-nowrap">
                  {entry.asset_type === 'cash' || entry.asset_type === 'online' 
                    ? formatCurrency(entry.amount) 
                    : entry.asset || getTamilAssetTypeLabel(entry.asset_type)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* FOOTER */}
      <div className="text-center mt-2 border-t border-dotted border-black pt-1 pb-1">
        <p className="text-[10px] mt-0.5 uppercase tracking-wide">Op: {operatorName} | {printedAt}</p>
        <p className="text-[9px] text-gray-600 mt-1 uppercase tracking-widest">Powered by Digital Moi Tech</p>
      </div>
    </div>
  );
});

export default ThermalReport;
